import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Trash2, Ban, Check, X, Clock, AlertTriangle,
  Loader2, FileText, Users, BarChart3, Bell, Eye
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white mb-0.5">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}

export default function AdminPdplPage() {
  const { authHeaders, API } = useAdminAuth();
  const { t } = useTranslation();
  const p = (key) => t(`admin.pdpl.${key}`);
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [breachDialog, setBreachDialog] = useState(false);
  const [breachForm, setBreachForm] = useState({ title: '', description: '', severity: 'medium', affected_users: 0 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [ovRes, reqRes, logRes, brRes] = await Promise.all([
        axios.get(`${API}/pdpl/overview`, authHeaders()),
        axios.get(`${API}/pdpl/requests`, authHeaders()),
        axios.get(`${API}/pdpl/audit-log`, authHeaders()),
        axios.get(`${API}/pdpl/breaches`, authHeaders()),
      ]);
      setOverview(ovRes.data);
      setRequests(reqRes.data.requests || []);
      setAuditLogs(logRes.data.logs || []);
      setBreaches(brRes.data.breaches || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleRequestAction = async (id, status) => {
    try {
      await axios.put(`${API}/pdpl/requests/${id}`, { status }, authHeaders());
      toast.success(status === 'completed' ? p('approved') : p('rejected'));
      fetchAll();
    } catch { toast.error('Action failed'); }
  };

  const handleReportBreach = async () => {
    if (!breachForm.title) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/pdpl/breach`, breachForm, authHeaders());
      toast.success(p('reported'));
      setBreachDialog(false);
      setBreachForm({ title: '', description: '', severity: 'medium', affected_users: 0 });
      fetchAll();
    } catch { toast.error('Failed'); }
    setSubmitting(false);
  };

  const handleBreachAction = async (id, updates) => {
    try {
      await axios.put(`${API}/pdpl/breach/${id}`, updates, authHeaders());
      toast.success(updates.sdaia_notified ? p('notified') : p('resolved'));
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const severityLabel = (s) => p(s);

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    </AdminLayout>
  );

  const ov = overview || { requests: {}, consent: {}, audit_logs_count: 0 };
  const tabs = [
    { key: 'overview', label: p('tabOverview'), icon: BarChart3 },
    { key: 'requests', label: p('tabRequests'), icon: FileText },
    { key: 'breaches', label: p('tabBreaches'), icon: AlertTriangle },
    { key: 'audit', label: p('tabAudit'), icon: Eye },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-pdpl-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="pdpl-title">{p('title')}</h1>
              <p className="text-xs text-slate-400">{p('subtitle')}</p>
            </div>
          </div>
          <Button onClick={() => setBreachDialog(true)} className="bg-red-600 hover:bg-red-700 text-white gap-2 rounded-lg" data-testid="report-breach-btn">
            <AlertTriangle className="w-4 h-4" /> {p('reportBreach')}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0d1224] p-1 rounded-xl border border-slate-800/60" data-testid="pdpl-tabs">
          {tabs.map(tb => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === tb.key ? 'bg-slate-700/50 text-white' : 'text-slate-400 hover:text-slate-300'}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={FileText} label={p('totalRequests')} value={ov.requests.total || 0} color="bg-blue-600" testId="stat-total-requests" />
              <StatCard icon={Clock} label={p('pending')} value={ov.requests.pending || 0} color="bg-amber-600" testId="stat-pending" />
              <StatCard icon={Trash2} label={p('deletionRequests')} value={ov.requests.deletion || 0} color="bg-red-600" testId="stat-deletions" />
              <StatCard icon={Ban} label={p('objections')} value={ov.requests.objection || 0} color="bg-orange-600" testId="stat-objections" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label={p('totalUsers')} value={ov.consent.total_users || 0} color="bg-indigo-600" testId="stat-users" />
              <StatCard icon={Bell} label={p('marketingOptIn')} value={ov.consent.marketing_opted_in || 0} color="bg-emerald-600" testId="stat-marketing" />
              <StatCard icon={BarChart3} label={p('analyticsOptIn')} value={ov.consent.analytics_opted_in || 0} color="bg-cyan-600" testId="stat-analytics" />
              <StatCard icon={Eye} label={p('auditLogs')} value={ov.audit_logs_count || 0} color="bg-purple-600" testId="stat-audit" />
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {tab === 'requests' && (
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-800/60">
              <h3 className="text-sm font-bold text-white">{p('dataSubjectRequests')} ({requests.length})</h3>
            </div>
            {requests.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">{p('noRequests')}</div>
            ) : (
              <div className="divide-y divide-slate-800/40">
                {requests.map(req => (
                  <div key={req.id} className="p-4 flex items-center justify-between" data-testid={`admin-req-${req.id}`}>
                    <div className="flex items-center gap-3">
                      {req.type === 'deletion' ? <Trash2 className="w-4 h-4 text-red-400" /> : <Ban className="w-4 h-4 text-amber-400" />}
                      <div>
                        <p className="text-sm text-white font-medium">{req.user_name} <span className="text-slate-500 text-xs">({req.user_email})</span></p>
                        <p className="text-[11px] text-slate-500">{req.type === 'deletion' ? p('deletionRequest') : p('objection')} - {new Date(req.created_at).toLocaleDateString('ar-SA')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={req.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : req.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                        {req.status === 'pending' ? p('pending') : req.status === 'completed' ? p('approved') : p('rejected')}
                      </Badge>
                      {req.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => handleRequestAction(req.id, 'completed')} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`approve-${req.id}`}>
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRequestAction(req.id, 'rejected')} className="h-7 border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid={`reject-${req.id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Breaches Tab */}
        {tab === 'breaches' && (
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-800/60">
              <h3 className="text-sm font-bold text-white">{p('dataBreaches')} ({breaches.length})</h3>
            </div>
            {breaches.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">{p('noBreaches')}</div>
            ) : (
              <div className="divide-y divide-slate-800/40">
                {breaches.map(b => (
                  <div key={b.id} className="p-4" data-testid={`breach-${b.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${b.severity === 'critical' ? 'text-red-400' : b.severity === 'high' ? 'text-orange-400' : 'text-amber-400'}`} />
                        <span className="text-sm font-medium text-white">{b.title}</span>
                        <Badge className={`text-[10px] ${b.severity === 'critical' ? 'bg-red-500/20 text-red-400' : b.severity === 'high' ? 'bg-orange-500/20 text-orange-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {severityLabel(b.severity)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {!b.sdaia_notified && (
                          <Button size="sm" onClick={() => handleBreachAction(b.id, { sdaia_notified: true })} className="h-7 bg-blue-600 hover:bg-blue-700 text-[10px] gap-1" data-testid={`notify-sdaia-${b.id}`}>
                            <Bell className="w-3 h-3" /> {p('notifySdaia')}
                          </Button>
                        )}
                        {b.status !== 'resolved' && (
                          <Button size="sm" onClick={() => handleBreachAction(b.id, { status: 'resolved' })} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-[10px]" data-testid={`resolve-${b.id}`}>
                            {p('resolve')}
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mb-2">{b.description}</p>
                    <div className="flex gap-4 text-[10px] text-slate-500">
                      <span>{p('affected')}: {b.affected_users} {p('users')}</span>
                      <span>{p('sdaia')}: {b.sdaia_notified ? p('notified') : p('pendingNotif')}</span>
                      <span>{p('deadline')}: {new Date(b.sdaia_deadline).toLocaleString('ar-SA')}</span>
                      <span>{p('status')}: {b.status === 'resolved' ? p('resolved') : p('reported')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Audit Log Tab */}
        {tab === 'audit' && (
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-800/60">
              <h3 className="text-sm font-bold text-white">{p('privacyAuditLog')} ({auditLogs.length})</h3>
            </div>
            {auditLogs.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">{p('noAuditEntries')}</div>
            ) : (
              <div className="divide-y divide-slate-800/40">
                {auditLogs.map(log => (
                  <div key={log.id} className="px-4 py-3 flex items-center justify-between" data-testid={`admin-audit-${log.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${log.action === 'deletion_request' ? 'bg-red-400' : log.action === 'data_export' ? 'bg-blue-400' : log.action === 'consent_update' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      <div>
                        <p className="text-xs text-white">{log.action}</p>
                        <p className="text-[10px] text-slate-500">{log.details}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-500">{new Date(log.timestamp).toLocaleString('ar-SA')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Report Breach Dialog */}
        <Dialog open={breachDialog} onOpenChange={setBreachDialog}>
          <DialogContent className="bg-[#0d1224] border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" /> {p('breachDialogTitle')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1.5">{p('breachTitle')}</label>
                <Input value={breachForm.title} onChange={e => setBreachForm({ ...breachForm, title: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white" placeholder={p('breachTitlePlaceholder')} data-testid="breach-title-input" />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5">{p('breachDesc')}</label>
                <textarea value={breachForm.description} onChange={e => setBreachForm({ ...breachForm, description: e.target.value })}
                  className="w-full h-20 rounded-md bg-slate-900 border border-slate-700 text-white text-sm p-2" placeholder={p('breachDescPlaceholder')} data-testid="breach-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">{p('severity')}</label>
                  <select value={breachForm.severity} onChange={e => setBreachForm({ ...breachForm, severity: e.target.value })}
                    className="w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm p-2" data-testid="breach-severity">
                    <option value="low">{p('low')}</option>
                    <option value="medium">{p('medium')}</option>
                    <option value="high">{p('high')}</option>
                    <option value="critical">{p('critical')}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">{p('affectedUsers')}</label>
                  <Input type="number" value={breachForm.affected_users} onChange={e => setBreachForm({ ...breachForm, affected_users: parseInt(e.target.value) || 0 })}
                    className="bg-slate-900 border-slate-700 text-white" data-testid="breach-users-input" />
                </div>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-[11px] text-red-400">{p('sdaiaWarning')}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBreachDialog(false)} className="border-slate-700 text-slate-300">{p('cancel')}</Button>
              <Button onClick={handleReportBreach} disabled={submitting || !breachForm.title} className="bg-red-600 hover:bg-red-700" data-testid="submit-breach-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : p('submitBreach')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
